import { Chapter, Variant } from 'js-slang/dist/langs';

import { AuthProviderType } from './AuthHelper';

function isTrue(value?: string, defaultTo?: boolean): boolean {
  return typeof value === 'undefined' && typeof defaultTo !== 'undefined'
    ? defaultTo
    : typeof value === 'string' && value.toUpperCase() === 'TRUE';
}

const isTest = process.env.NODE_ENV === 'test';

const sourceAcademyVersion = process.env.REACT_APP_VERSION || 'local';
const sourceAcademyEnvironment = process.env.REACT_APP_ENVIRONMENT || 'dev';
const sourceAcademyDeploymentName = isTest
  ? 'Source Academy @ NUS'
  : process.env.REACT_APP_DEPLOYMENT_NAME || 'Source Academy';
const showResearchPrompt = isTest || isTrue(process.env.REACT_APP_SHOW_RESEARCH_PROMPT);
// const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://source-academy.it.uu.se/api';
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const cadetLoggerUrl = isTest ? undefined : process.env.REACT_APP_CADET_LOGGER;
const cadetLoggerInterval = parseInt(process.env.REACT_APP_CADET_LOGGER_INTERVAL || '10000', 10);
const useBackend = !isTest && isTrue(process.env.REACT_APP_USE_BACKEND);
/**
 * Whether or not attach cookies to every API call. Useful for load balancers that
 * route requests based on cookies (e.g. for sticky sessions).
 */
const forwardLoadBalancerCookies = isTrue(process.env.REACT_APP_FORWARD_LOAD_BALANCER_COOKIES);
const useEmptyAssetPrefix = isTrue(process.env.REACT_APP_USE_EMPTY_ASSET_PREFIX);
const defaultSourceChapter = Chapter.SOURCE_4;
const defaultSourceVariant = Variant.DEFAULT;
const defaultQuestionId = 0;
const maxBrowseIndex = 50;
const mobileBreakpoint = 768;
const urlShortenerBase = process.env.REACT_APP_URL_SHORTENER_BASE;
const urlShortenerSignature = process.env.REACT_APP_URL_SHORTENER_SIGNATURE;
const moduleBackendUrl = process.env.REACT_APP_MODULE_BACKEND_URL || 'modules';
const sharedbBackendUrl = process.env.REACT_APP_SHAREDB_BACKEND_URL || '';
const playgroundOnly = !isTest && isTrue(process.env.REACT_APP_PLAYGROUND_ONLY, true);
const hideFinalizeSubmission = isTrue(process.env.REACT_APP_HIDE_FINALIZE_SUBMISSION);
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY;
const googleAppId = process.env.REACT_APP_GOOGLE_APP_ID;
const githubClientId = process.env.REACT_APP_GITHUB_CLIENT_ID || '';
const githubOAuthProxyUrl = process.env.REACT_APP_GITHUB_OAUTH_PROXY_URL || '';
const sicpBackendUrl =
  process.env.REACT_APP_SICPJS_BACKEND_URL || 'https://sicp.sourceacademy.org/';
const javaPackagesUrl = 'https://source-academy.github.io/modules/java/java-packages/src/';
const workspaceSettingsLocalStorageKey = 'workspace-settings';
const collabSessionIdLocalStorageKey = 'playground-session-id';

// For achievements feature (CA - Continual Assessment)
// TODO: remove dependency of the ca levels on the env file
const caFulfillmentLevel = isTest
  ? 24
  : parseInt(process.env.REACT_APP_CA_FULFILLMENT_LEVEL || '0');

type AuthProvider = {
  name: string;
  endpoint: string;
  isDefault: boolean;
  type: AuthProviderType;
  screen: 'default' | 'nus';
};

const authProviders: Map<string, AuthProvider> = new Map();

// Helper to read screen configuration, defaulting to 'default'
const getProviderScreen = (prefix: string, i: number): 'default' | 'nus' => {
  const screen = process.env[`${prefix}${i}_SHOW_FOR_SCREEN`]?.trim().toLowerCase();
  return screen === 'nus' ? 'nus' : 'default';
};

for (let i = 1; ; ++i) {
  const id = process.env[`REACT_APP_OAUTH2_PROVIDER${i}`];
  if (!id) {
    break;
  }

  const name = process.env[`REACT_APP_OAUTH2_PROVIDER${i}_NAME`] || 'Unnamed provider';
  const endpoint = process.env[`REACT_APP_OAUTH2_PROVIDER${i}_ENDPOINT`] || '';
  const screen = getProviderScreen('REACT_APP_OAUTH2_PROVIDER', i);

  authProviders.set(id, {
    name,
    endpoint,
    isDefault: i === 1,
    type: AuthProviderType.OAUTH2,
    screen,
  });
}

for (let i = 1; ; ++i) {
  const id = process.env[`REACT_APP_CAS_PROVIDER${i}`];
  if (!id) {
    break;
  }

  const name = process.env[`REACT_APP_CAS_PROVIDER${i}_NAME`] || 'Unnamed provider';
  const endpoint = process.env[`REACT_APP_CAS_PROVIDER${i}_ENDPOINT`] || '';
  const screen = getProviderScreen('REACT_APP_CAS_PROVIDER', i);

  authProviders.set(id, { name, endpoint, isDefault: false, type: AuthProviderType.CAS, screen });
}

for (let i = 1; ; ++i) {
  const id = process.env[`REACT_APP_SAML_PROVIDER${i}`];
  if (!id) {
    break;
  }

  const name = process.env[`REACT_APP_SAML_PROVIDER${i}_NAME`] || 'Unnamed provider';
  const endpoint = process.env[`REACT_APP_SAML_PROVIDER${i}_ENDPOINT`] || '';
  const screen = getProviderScreen('REACT_APP_SAML_PROVIDER', i);

  authProviders.set(id, {
    name,
    endpoint,
    isDefault: false,
    type: AuthProviderType.SAML_SSO,
    screen,
  });
}

// Computed providers filtered by screen
const defaultAuthProviders = new Map(
  [...authProviders.entries()].filter(([, p]) => p.screen === 'default'),
);
const nusAuthProviders = new Map(
  [...authProviders.entries()].filter(([, p]) => p.screen === 'nus'),
);

const hasDefaultAuthProviders = defaultAuthProviders.size > 0;
const hasNusAuthProviders = nusAuthProviders.size > 0;

const featureFlags = {
  enableSicpChatbot: isTrue(process.env.REACT_APP_FEATURE_ENABLE_SICP_CHATBOT),
};

// Deployment-pinned Conductor settings; `undefined` leaves the corresponding feature flag under
// the user's control on /features.
const conductorConfig = {
  enable: process.env.REACT_APP_CONDUCTOR_ENABLE
    ? isTrue(process.env.REACT_APP_CONDUCTOR_ENABLE)
    : undefined,
  languageDirectoryUrl: process.env.REACT_APP_LANGUAGE_DIRECTORY_URL || undefined,
  pluginDirectoryUrl: process.env.REACT_APP_PLUGIN_DIRECTORY_URL || undefined,
  modulesDirectoryUrl: process.env.REACT_APP_MODULES_DIRECTORY_URL || undefined,
};

export enum Links {
  githubIssues = 'https://github.com/source-academy/frontend/issues',
  githubOrg = 'https://github.com/source-academy',
  about = 'https://about.sourceacademy.org',

  resourcesForEducators = 'https://about.sourceacademy.org/educator/README.html',
  resourcesForLearners = 'https://about.sourceacademy.org/learner/README.html',

  sourceAcademyAssets = 'https://source-academy-assets.s3-ap-southeast-1.amazonaws.com/',
  sourceDocs = 'https://docs.sourceacademy.org/',
  pythonDocs = 'https://docs.sourceacademy.org/python/',
  moduleDocs = 'https://source-academy.github.io/modules/documentation/',
  textbook = 'https://sourceacademy.org/sicpjs/',
  playground = 'https://sourceacademy.org/playground',
  textbookChapter2_2 = 'https://sourceacademy.org/sicpjs/2.2',
  textbookChapter3_2 = 'https://sourceacademy.org/sicpjs/3.2',
  aceHotkeys = 'https://github.com/ajaxorg/ace/wiki/Default-Keyboard-Shortcuts',
  sourceHotkeys = 'https://github.com/source-academy/frontend/wiki/Source-Academy-Keyboard-Shortcuts',

  ecmaScript_2021 = 'https://262.ecma-international.org/12.0/',

  vscode = 'vscode://source-academy.source-academy/sso',
}

const Constants = {
  sourceAcademyVersion,
  sourceAcademyEnvironment,
  sourceAcademyDeploymentName,
  showResearchPrompt,
  backendUrl,
  cadetLoggerUrl,
  useBackend,
  forwardLoadBalancerCookies,
  useEmptyAssetPrefix,
  defaultSourceChapter,
  defaultSourceVariant,
  defaultQuestionId,
  maxBrowseIndex,
  mobileBreakpoint,
  urlShortenerBase,
  urlShortenerSignature,
  moduleBackendUrl,
  authProviders,
  hasNusAuthProviders,
  nusAuthProviders,
  hasDefaultAuthProviders,
  defaultAuthProviders,
  playgroundOnly,
  hideFinalizeSubmission,
  sentryDsn,
  googleClientId,
  googleApiKey,
  googleAppId,
  githubClientId,
  githubOAuthProxyUrl,
  sharedbBackendUrl,
  cadetLoggerInterval,
  sicpBackendUrl,
  javaPackagesUrl,
  workspaceSettingsLocalStorageKey,
  collabSessionIdLocalStorageKey,
  caFulfillmentLevel,
  featureFlags,
  conductorConfig,
};

export default Constants;
